'use client';

import {
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query';
import { atom } from 'jotai';
import { fetchCommentRead } from 'lib/comment-read';
import type { CommentList, CommentRequest, CommentViewer } from 'lib/comments';

export const commentOpenAtom = atom<Record<string, boolean>>({});

export type CommentMutationResult = {
  success?: boolean;
  challengeId?: string;
  viewer?: CommentViewer;
  canModerate?: boolean;
};

type WithoutPostId<T> = T extends { postId: string }
  ? Omit<T, 'postId'>
  : never;

export function updateCommentProfile(
  client: QueryClient,
  postId: string,
  profile: { name: string; avatar?: string | null },
) {
  client.setQueriesData<InfiniteData<CommentList>>(
    { queryKey: ['comments', postId] },
    (previous) =>
      previous
        ? {
            ...previous,
            pages: previous.pages.map((page) => ({
              ...page,
              viewer: page.viewer
                ? {
                    ...page.viewer,
                    name: profile.name.trim(),
                    ...(profile.avatar !== undefined
                      ? { avatar: profile.avatar }
                      : {}),
                  }
                : null,
              comments: page.comments.map((comment) =>
                comment.canEdit
                  ? {
                      ...comment,
                      author: {
                        ...comment.author,
                        name: profile.name.trim(),
                        ...(profile.avatar !== undefined
                          ? { avatar: profile.avatar }
                          : {}),
                      },
                    }
                  : comment,
              ),
            })),
          }
        : previous,
  );
}

export function finishCommentMutation(
  client: QueryClient,
  postId: string,
  input: WithoutPostId<CommentRequest>,
  result?: CommentMutationResult,
) {
  if (input.action === 'logout') {
    client.setQueriesData<InfiniteData<CommentList>>(
      { queryKey: ['comments'] },
      (previous) =>
        previous
          ? {
              ...previous,
              pages: previous.pages.map((page) => ({
                ...page,
                viewer: null,
                canModerate: false,
                comments: page.comments.map((comment) => ({
                  ...comment,
                  canEdit: false,
                  canDelete: false,
                  liked: false,
                })),
              })),
            }
          : previous,
    );
    client.removeQueries({ queryKey: ['my-comments'] });
    client.removeQueries({ queryKey: ['comment-notifications'] });
  } else if (input.action === 'verify-code' && result?.viewer) {
    const viewer = result.viewer;
    client.setQueriesData<InfiniteData<CommentList>>(
      { queryKey: ['comments', postId] },
      (previous) =>
        previous
          ? {
              ...previous,
              pages: previous.pages.map((page) => ({
                ...page,
                viewer,
                canModerate: !!result.canModerate,
                comments: page.comments.map((comment) => {
                  const sameUser = page.viewer?.userId === viewer.userId;
                  const canEdit = sameUser && comment.canEdit;
                  return {
                    ...comment,
                    canEdit,
                    canDelete:
                      !comment.deleted && (!!result.canModerate || canEdit),
                    liked: sameUser && comment.liked,
                  };
                }),
              })),
            }
          : previous,
    );
  } else if (input.action === 'vote') {
    // Keep the accepted vote visible when a long history refresh is queued.
    client.setQueriesData<InfiniteData<CommentList>>(
      { queryKey: ['comments', postId] },
      (previous) =>
        previous
          ? {
              ...previous,
              pages: previous.pages.map((page) => ({
                ...page,
                comments: page.comments.map((comment) =>
                  comment.id === input.commentId
                    ? {
                        ...comment,
                        liked: input.liked,
                        likes: Math.max(
                          0,
                          comment.likes +
                            (comment.liked === input.liked
                              ? 0
                              : input.liked
                                ? 1
                                : -1),
                        ),
                      }
                    : comment,
                ),
              })),
            }
          : previous,
    );
  } else if (input.action === 'profile') {
    updateCommentProfile(client, postId, {
      name: input.name,
      ...(input.removeAvatar ? { avatar: null } : {}),
    });
  } else if (input.action === 'settings') {
    client.setQueriesData<InfiniteData<CommentList>>(
      { queryKey: ['comments', postId] },
      (previous) =>
        previous
          ? {
              ...previous,
              pages: previous.pages.map((page) => ({
                ...page,
                viewer: page.viewer
                  ? {
                      ...page.viewer,
                      replyNotifications: input.replyNotifications,
                      likeNotifications: input.likeNotifications,
                    }
                  : null,
              })),
            }
          : previous,
    );
  }
  // POST completion must not wait for paced, potentially long history refreshes.
  void Promise.all([
    client.invalidateQueries({ queryKey: ['comments'] }),
    client.invalidateQueries({ queryKey: ['my-comments'] }),
    client.invalidateQueries({ queryKey: ['comment-notifications'] }),
  ]);
}

export async function fetchCommentList(
  postId: string,
  sort: string,
  page: number,
  manage: boolean,
  rootId?: string,
  signal?: AbortSignal,
): Promise<CommentList> {
  const params = new URLSearchParams({
    postId,
    sort,
    page: String(page),
    manage: String(manage),
  });
  if (rootId) params.set('rootId', rootId);
  return fetchCommentRead<CommentList>(
    `/.quotion/api/comments?${params}`,
    signal,
  );
}

export function useCommentMutation(postId: string, manage = false) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (input: WithoutPostId<CommentRequest>) => {
      const response = await fetch(`/.quotion/api/comments?manage=${manage}`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...input, postId }),
      });
      const data = (await response.json()) as CommentMutationResult & {
        error?: string;
      };
      if (!response.ok) throw new Error(data.error || 'request-failed');
      return data;
    },
    onSuccess: (data, input) =>
      finishCommentMutation(client, postId, input, data),
  });
}
